import { useFetchHotel } from "./redux/fetchHotel";
import { getBookingStatusLabel } from "../../common/bookingStatus";
import React, { useEffect, useState, useCallback, useRef } from "react";
// import PropTypes from 'prop-types';
import {} from "./redux/hooks";
import {
  Box,
  Header,
  Heading,
  Button,
  DataTable,
  Text,
  Pagination,
} from "grommet";
import { View, Trash } from "grommet-icons";
import GuestService from "../../services/guest.service";
import { capitalize } from "@common/utils";
import {
  BookingDates,
  AppInstalled,
  NoPhoneIcon,
  NoEmailIcon,
  BookingStatus,
  NoResults,
  SmsUnreachableIcon,
} from "@shared/";
import { presentAlertPrompt } from "@shared/Alert";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const mapBooking = (booking, serviceType, t) => ({
  id: booking._id,
  bookingId: booking.bookingId,
  name: capitalize(booking.firstname) + " " + capitalize(booking.lastname),
  appInstalled: booking.appInstalled,
  room: booking.roomNumber ? booking.roomNumber : "-",
  dates: (
    <BookingDates
      checkinDate={booking.checkinDate}
      checkoutDate={booking.checkoutDate}
    />
  ),
  status: booking.status,
  statusName: getBookingStatusLabel(booking.status, serviceType, t),
  phone: booking.phone,
  email: booking.email,
  smsUnreachable: !!booking.smsUnreachable,
  emailUnreachable: !!booking.emailUnreachable,
  commsFailed: !!booking.commsFailed,
});

export default function ArchivedPage() {
  const { hotel } = useFetchHotel();
  const { t } = useTranslation();
  const [guestList, setGuestList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({ field: null, term: "" });
  const searchDebounceRef = useRef(null);

  const fetchGuests = useCallback(
    (page = 1, searchField = null, searchTerm = "") => {
      presentLoading();
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (searchField && searchTerm.trim()) {
        params.searchField = searchField;
        params.searchTerm = searchTerm.trim();
      }
      GuestService.getBookings("archived", params).then(
        (response) => {
          const { data, total } = response.data;
          const list = data.map(booking => mapBooking(booking, hotel?.serviceType, t));
          dismissLoading();
          setLoading(false);
          setGuestList(list);
          setTotalItems(total);
        },
        (error) => {
          dismissLoading();
          setLoading(false);
          setGuestList([]);
          setTotalItems(0);
        },
      );
    },
    [hotel?.serviceType, t],
  );

  const columns = [
    {
      property: "bookingId",
      header: "Ref.",
      search: true,
    },
    {
      property: "name",
      header: "Name",
      search: true,
      render: (datum) => (
        <Box direction="row" gap="small" align="center">
          <Text>{datum.name}</Text>
          {datum.appInstalled && <AppInstalled pkey={datum.id} />}
          {!datum.phone && !datum.appInstalled && (
            <NoPhoneIcon pkey={`nophone-${datum.id}`} />
          )}
          {datum.phone && datum.smsUnreachable && (
            <SmsUnreachableIcon
              pkey={`smsunreachable-${datum.id}`}
              phone={datum.phone}
              color={
                datum.email && !datum.emailUnreachable
                  ? "status-warning"
                  : "status-critical"
              }
            />
          )}
          {!datum.email && <NoEmailIcon pkey={`noemail-${datum.id}`} />}
        </Box>
      ),
    },
    {
      property: "room",
      header: "Room",
      align: "center",
      search: true,
    },
    {
      property: "dates",
      header: "Booking Dates",
      // render: datum =>
      //   datum.date && new Date(datum.date).toLocaleDateString('en-US'),
      align: "center",
    },
    {
      property: "statusName",
      header: "Status",
      align: "center",
      search: true,
      render: (datum) => (
        <BookingStatus status={datum.status} commsFailed={datum.commsFailed} />
      ),
    },
    {
      property: "actions",
      header: "Actions",
      align: "center",
      render: (datum) => (
        <Box direction="row" gap="medium">
          <Button
            hoverIndicator="brand"
            icon={<View size="small" />}
            size="small"
            primary
            color="neutral-2"
            onClick={() => handleGuestDetails(datum.id)}
          />
          {datum.status.slug !== "cancelled" && (
            <Button
              hoverIndicator="dark-1"
              icon={<Trash size="small" />}
              size="small"
              primary
              color="status-critical"
              onClick={() => handleRemoveBooking(datum.id)}
            />
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchGuests(currentPage, search.field, search.term);
  }, [currentPage, search, fetchGuests]);

  const handleSearch = useCallback((searchObj) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const entry = Object.entries(searchObj).find(([, v]) => v && v.trim());
      const field = entry ? entry[0] : null;
      const term = entry ? entry[1] : "";
      setSearch({ field, term });
      setCurrentPage(1);
    }, 400);
  }, []);

  const handlePageChange = ({ page }) => {
    setCurrentPage(page);
  };

  const handleGuestDetails = (guestId) => {
    window.location.href = "archived/" + guestId;
  };

  const handleRemoveBooking = (guestId) => {
    function removeGuest() {
      GuestService.deleteBooking(guestId).then(
        (response) => {
          presentToastSuccess("Reservation successfully removed.");
          fetchGuests(currentPage, search.field, search.term);
        },
        (error) => {
          presentToastErrorContent(error);
          fetchGuests(currentPage, search.field, search.term);
        },
      );
    }

    presentAlertPrompt({
      title: "Are you sure?",
      message:
        "This will cancel the reservation only on LOXE's system, not on your PMS.",
      onOK: removeGuest,
      buttonOKText: "Delete",
      buttonOKColor: "status-critical",
    });
  };

  return (
    <Box align="center" justify="start" fill pad="medium">
      <Header
        align="center"
        direction="row"
        justify="between"
        gap="medium"
        fill="horizontal"
      >
        <Heading level="2">
          {totalItems > 0
            ? t("archived.titleWithCount", { count: totalItems })
            : t("archived.title")}
        </Heading>
      </Header>
      <Box fill="horizontal">
        {!loading && !guestList.length && !search.term ? (
          <NoResults>{t("common.emptyList")}</NoResults>
        ) : (
          <Box>
            <DataTable
              columns={columns}
              data={guestList}
              primaryKey="id"
              onClickRow={(event) => {
                if (
                  event.target.localName !== "button" &&
                  event.target.localName !== "svg" &&
                  event.target.localName !== "path"
                )
                  handleGuestDetails(event.datum.id);
              }}
              onSearch={handleSearch}
            />
            {totalItems > PAGE_SIZE && (
              <Box align="center" pad={{ top: "medium" }}>
                <Pagination
                  numberItems={totalItems}
                  step={PAGE_SIZE}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

ArchivedPage.propTypes = {};
ArchivedPage.defaultProps = {};
